// Libraries
import { Injectable } from '@angular/core';
import { Response } from '@angular/http';

// Interfaces
import { IbApiConfig } from './../interfaces/ib-api-config.interface';
import { BaseModel } from './../interfaces/base-model.interface';
import { ApiConfig } from './../config/api-config';

/**
 * `BaseService` implements the basic REST interaction of any service with the api.
 *
 * @typeparam R Represents the resource to be handled.
 */
@Injectable()
export abstract class BaseService<R extends BaseModel> {
  /**
   * Current api configuration.
   */
  protected config: IbApiConfig;
  /**
   * Resource name.
   */
  protected resourceName: string;
  /**
   * Current api url.
   */
  protected apiUrl: string;

  /**
   * Creates a new `BaseService` instance.
   * @param httpService Http service to make requests.
   * @param rName Name of resource.
   * @param rNameSingular Singular name of resource.
   */
  constructor(
    protected httpService: any,
    protected rName: string,
    protected apiConfig: ApiConfig
  ) {
    this.resourceName = rName;
    this.apiUrl = 'api/v' + apiConfig.version + '/' + this.resourceName;
  }

  // Public methods
  /**
   * Get a list of `R` objects
   * @param queryParams Optional query parameters
   * @returns An promise with an array of `R` objects.
   */
  public get(queryParams?: any): Promise<R[]> {
    return this.httpService.get(this.apiUrl, { search: queryParams })
      .toPromise()
      .then(response => this.processListData(response) as R[])
      .catch( this.handleError );
  }

  /**
   * Get one `R` object
   * @param id Resource id
   * @returns An promise with an array of `R` objects.
   */
  public getOne(id: number): Promise<R> {
    return this.httpService.get(`${this.apiUrl}/${id}`)
      .toPromise()
      .then(response => this.processData(response) as R)
      .catch( this.handleError );
  }

  /**
   * Create one `R` object.
   * @param data Resource data data
   */
  public post(data: R): Promise<R> {
    return this.httpService.post(this.apiUrl, this.generateBody(data))
      .toPromise()
      .then(response => this.processData(response) as R)
      .catch( this.handleError );
  }

  /**
   * Delete on `R` object.
   * @param id Id of resource name `R`.
   */
  public delete(id: number): Promise<void> {
    return this.httpService.delete(`${this.apiUrl}/${id}`)
      .toPromise()
      .then(() => null)
      .catch( this.handleError );
  }

  /**
   * Update one `R` object.
   * @param data Resource data data
   */
  public put(data: R): Promise<R> {
    return this.httpService.put(`${this.apiUrl}/${data.id}`, this.generateBody(data))
      .toPromise()
      .then(() => data)
      .catch( this.handleError );
  }

  /**
   * Proccess response data from api request when response is list.
   * @param res Response object
   * @returns An JSON object.
   */
  protected processListData(res: Response): any {
    let jsonResponse = res.json();
    // Extract resource list
    let resources = jsonResponse.data || [];
    // Extract meta data
    if (jsonResponse.hasOwnProperty('meta')) {
      resources._meta = jsonResponse.meta;
    }

    return resources;
  }

  /**
   * Proccess response data from api request.
   * @param res Response object
   * @returns An JSON object.
   */
  protected processData(res: Response): any {
    let jsonResponse = res.json();
    // Extract resource data
    let resource = jsonResponse.data || {};

    return resource;
  }

  /**
   * Catch api request errors.
   * @param error Request error.
   * @returns Promise with error message.
   */
  protected handleError(error: any): Promise<any> {
    return Promise.reject(error.message || error);
  }

  /**
   * Convert data object to string.
   * @param data Object to append to body.
   * @returns String with object data.
   */
  protected generateBody(data: R): string {
    return JSON.stringify(data);
  }
}



// WEBPACK FOOTER //
// ./~/angular2-template-loader!./~/@angularclass/hmr-loader!./~/angular-router-loader/src!./~/tslint-loader!./src/app/woopa-ui/api/services/base.service.ts